<template>
  <div class="app-container">
    <div class="filter-container">
      <el-date-picker v-model="query.year" type="year" placeholder="年份" value-format="yyyy" class="filter-item" style="width: 110px;" />
      <el-select v-model="query.season" placeholder="周期" class="filter-item" style="width: 120px;">
        <el-option v-for="s in 4" :key="s" :label="'第' + s + '季度'" :value="s" />
        <el-option :key="0" label="年度平均" :value="0" />
      </el-select>
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="loadAll">
        查询
      </el-button>
      <!-- 计算入口仅 KAOHE_CAL 角色可见（后端 /perf/calc 同步按角色校验，无此角色调用直接拒绝） -->
      <el-button v-if="canCalc" v-waves class="filter-item" type="warning" icon="el-icon-s-operation" :loading="calculating" :disabled="!periodOpen" @click="doCalc">
        计算结果
      </el-button>
      <span v-if="canCalc" class="filter-item" style="font-size: 13px;" :style="{ color: periodOpen ? '#909399' : '#E6A23C' }">{{ periodOpen ? '计算会按当前明细重新生成并覆盖本季度结果' : '当前所选期次未开启或已关闭，不能计算结果' }}</span>
    </div>

    <!-- 部门考核结果分节：统计管理员看全部；部门负责人/分管领导看自己负责或分管的部门行；其他员工不展示 -->
    <div v-if="showDeptSection" class="perf-section-head">
      <h4 class="perf-section-title">部门考核结果</h4>
      <el-button type="primary" size="mini" icon="el-icon-download" plain @click="exportDept">导出</el-button>
    </div>
    <el-table
      v-if="showDeptSection"
      v-loading="deptLoading"
      :data="deptRows"
      border
      fit
      highlight-current-row
      style="width: 100%;"
    >
      <el-table-column label="部门" min-width="130" align="center">
        <template slot-scope="{row}">{{ row.deptName }}</template>
      </el-table-column>
      <el-table-column label="分类" width="90" align="center">
        <template slot-scope="{row}">{{ categoryText(row.category) }}</template>
      </el-table-column>
      <!-- 明细得分列对所有角色不展示（后端接口亦不返回明细字段，明细仅落库供审计） -->
      <el-table-column label="最终得分" width="110" align="center">
        <template slot-scope="{row}">
          <span class="final-score">{{ fmt(row.finalScore) }}</span>
        </template>
      </el-table-column>
    </el-table>

    <div class="perf-section-head" style="margin-top: 22px;">
      <h4 class="perf-section-title">人员考核结果</h4>
      <el-button type="primary" size="mini" icon="el-icon-download" plain @click="exportEmp">导出</el-button>
    </div>
    <!-- 部门筛选仅统计管理员有意义：其他人返回的数据已由后端按身份限定范围 -->
    <div v-if="canViewAll" class="filter-container" style="padding-bottom: 10px;">
      <el-select v-model="query.deptId" placeholder="按部门筛选" clearable class="filter-item" style="width: 180px;" @change="loadEmp">
        <el-option v-for="d in deptOptions" :key="d.deptId" :label="d.deptName" :value="d.deptId" />
      </el-select>
    </div>
    <el-table
      v-loading="empLoading"
      :data="pagedEmpRows"
      border
      fit
      highlight-current-row
      style="width: 100%;"
    >
      <el-table-column label="姓名" min-width="100" align="center">
        <template slot-scope="{row}">{{ row.employeeName }}</template>
      </el-table-column>
      <el-table-column label="部门" min-width="120" align="center">
        <template slot-scope="{row}">{{ row.deptName }}</template>
      </el-table-column>
      <el-table-column label="类型" width="80" align="center">
        <template slot-scope="{row}">
          <el-tag :type="row.empType === 'MID' ? 'warning' : (row.empType === 'EXEC' ? 'danger' : '')" size="small">
            {{ empTypeText(row.empType) }}
          </el-tag>
        </template>
      </el-table-column>
      <!-- 明细得分列对所有角色不展示（后端接口亦不返回明细字段，明细仅落库供审计） -->
      <el-table-column label="最终得分" width="110" align="center">
        <template slot-scope="{row}">
          <span class="final-score">{{ fmt(row.finalScore) }}</span>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      background
      style="margin-top: 12px; text-align: right;"
      layout="total, sizes, prev, pager, next, jumper"
      :total="sortedEmpRows.length"
      :page-sizes="[10, 20, 50, 100]"
      :current-page.sync="empPage"
      :page-size.sync="empSize"
    />

    <!-- 打分进度分节（仅统计管理员 KAOHE_TONGJI_MANANGE / 计算 KAOHE_CAL 可见；后端 /perf/progress 同步校验） -->
    <template v-if="canProgress">
      <div class="perf-section-head" style="margin-top: 22px;">
        <h4 class="perf-section-title">打分进度</h4>
        <el-button type="primary" size="mini" icon="el-icon-refresh" plain :loading="progressLoading" @click="loadProgress">刷新</el-button>
      </div>
      <el-alert v-if="Number(query.season) === 0" type="info" :closable="false"
        title="打分进度按具体季度统计：请在上方「周期」选择第 1~4 季度后查看（年度平均无进度语义）。" />
      <template v-else>
        <el-alert v-if="progress" type="info" :closable="false" style="margin-bottom: 12px;"
          :title="'本期共 ' + progress.people + ' 人需打分，已全部完成 ' + progress.donePeople + ' 人。仅统计已绑定登录账号的在职员工；「未配置」为该员工岗位未配置考核表、无法打分的任务数，需到岗位管理页补齐。'" />
        <div v-if="progress" style="font-weight: bold; margin: 6px 0; color: #606266;">每人明细（点行首箭头展开分场景）</div>
        <el-table v-loading="progressLoading" :data="progress ? progress.list : []" border fit size="mini" style="width: 100%;">
          <el-table-column type="expand">
            <template slot-scope="{row}">
              <el-table :data="row.scenes" border size="mini" style="width: 94%; margin: 0 auto;">
                <el-table-column label="打分场景" min-width="200" align="center" prop="sceneName" />
                <el-table-column label="应打" width="90" align="center" prop="total" />
                <el-table-column label="已打" width="90" align="center" prop="submitted" />
                <el-table-column label="状态" width="110" align="center">
                  <template slot-scope="s">
                    <el-tag size="mini" :type="s.row.submitted === s.row.total ? 'success' : (s.row.submitted === 0 ? 'info' : 'warning')">
                      {{ s.row.submitted === s.row.total ? '已完成' : (s.row.submitted === 0 ? '未开始' : '进行中') }}
                    </el-tag>
                  </template>
                </el-table-column>
              </el-table>
            </template>
          </el-table-column>
          <el-table-column label="姓名" width="110" align="center" prop="empName" />
          <el-table-column label="部门" min-width="140" align="center" prop="deptName" />
          <el-table-column label="应打" width="80" align="center" prop="total" />
          <el-table-column label="已打" width="80" align="center" prop="submitted" />
          <el-table-column label="未配置" width="90" align="center">
            <template slot-scope="{row}">
              <span :style="row.unconfigured > 0 ? 'color:#E6A23C;font-weight:bold;' : ''">{{ row.unconfigured }}</span>
            </template>
          </el-table-column>
          <el-table-column label="完成度" min-width="180" align="center">
            <template slot-scope="{row}">
              <el-progress :percentage="pct(row.submitted, row.total)" :status="row.done ? 'success' : undefined" />
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100" align="center">
            <template slot-scope="{row}">
              <el-tag size="mini" :type="row.done ? 'success' : (row.submitted === 0 ? 'info' : 'warning')">
                {{ row.done ? '已完成' : (row.submitted === 0 ? '未开始' : '进行中') }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </template>
  </div>
</template>

<script>
import { calcResults, getDeptResults, getEmpResults, getPeriods, getPerfViewScope, getProgress } from '../../api/performance'
import waves from '../../directive/waves'

export default {
  name: 'PerfResults',
  directives: { waves },
  data() {
    const now = new Date()
    return {
      query: {
        year: String(now.getFullYear()),
        season: Math.floor(now.getMonth() / 3) + 1,
        deptId: null
      },
      deptRows: [],
      empRows: [],
      deptOptions: [],
      deptLoading: false,
      empLoading: false,
      calculating: false,
      periods: [],
      scope: { viewAll: false, deptLeader: false, deptManager: false, employeeId: null, deptIds: [], deptNames: [] },
      empPage: 1,
      empSize: 10,
      progress: null,
      progressLoading: false
    }
  },
  created() {
    // 先取查看范围（管理员/部门负责人/普通员工），再取考核期并加载数据；范围接口失败按非管理员兜底
    getPerfViewScope().then(response => {
      this.scope = response.data || { viewAll: false }
    }).catch(() => {}).then(() => {
      this.initPeriods()
    })
  },
  computed: {
    /** 考核统计管理员（KAOHE_TONGJI_MANANGE）：可见全部部门/人员结果行并可用部门筛选（明细得分列对所有角色均不展示，均只见最终得分） */
    canViewAll() {
      return !!this.scope.viewAll
    },
    /** 是否展示「部门考核结果」分节：统计管理员看全部；部门负责人/分管领导看自己负责或分管的部门行；其他员工不展示 */
    showDeptSection() {
      return this.canViewAll || (this.scope.deptIds || []).length > 0
    },
    /** 是否可执行考核结果计算（需 KAOHE_CAL 角色；后端 /perf/calc 同步按角色校验） */
    canCalc() {
      const roles = this.$store.state.user.roles || []
      return roles.some(r => r.roleCode === 'KAOHE_CAL')
    },
    /** 是否可查看打分进度（需 KAOHE_TONGJI_MANANGE 或 KAOHE_CAL 角色；后端 /perf/progress 同步校验） */
    canProgress() {
      const roles = this.$store.state.user.roles || []
      return roles.some(r => r.roleCode === 'KAOHE_TONGJI_MANANGE' || r.roleCode === 'KAOHE_CAL')
    },
    /** 当前所选期次是否开启中（仅开启中的期次允许计算结果） */
    periodOpen() {
      const p = this.periods.find(x => Number(x.year) === Number(this.query.year) && Number(x.season) === Number(this.query.season))
      return !!p && p.enabled === 1
    },
    /** 人员结果按部门排序：部门顺序与部门结果表一致；同部门内中层→员工→高管，其余保持后端原序 */
    sortedEmpRows() {
      const deptIdx = {}
      this.deptOptions.forEach((d, i) => { deptIdx[d.deptId] = i })
      const typeIdx = { MID: 0, STAFF: 1, EXEC: 2 }
      const last = this.deptOptions.length
      return this.empRows.map((r, i) => ({ r, i }))
        .sort((a, b) => {
          const da = deptIdx[a.r.deptId] === undefined ? last : deptIdx[a.r.deptId]
          const db = deptIdx[b.r.deptId] === undefined ? last : deptIdx[b.r.deptId]
          if (da !== db) return da - db
          const ta = typeIdx[a.r.empType] === undefined ? 9 : typeIdx[a.r.empType]
          const tb = typeIdx[b.r.empType] === undefined ? 9 : typeIdx[b.r.empType]
          if (ta !== tb) return ta - tb
          return a.i - b.i
        })
        .map(x => x.r)
    },
    /** 人员结果当前页数据（客户端分页） */
    pagedEmpRows() {
      const start = (this.empPage - 1) * this.empSize
      return this.sortedEmpRows.slice(start, start + this.empSize)
    }
  },
  methods: {
    /** 取考核期列表：存在开启中的期次时默认选中它（与我的任务/考核配置一致）；无开启期次或取失败时保持当前自然季度 */
    initPeriods() {
      getPeriods().then(response => {
        this.periods = response.data || []
        const open = this.periods.find(p => p.enabled === 1)
        if (open) {
          this.query.year = String(open.year)
          this.query.season = open.season
        }
        this.loadAll()
      }).catch(() => {
        this.loadAll()
      })
    },
    fmt(v) {
      return v === null || v === undefined ? '-' : v
    },
    categoryText(c) {
      return { BIZ: '业务类', RISK: '风控类', COMPRE: '综合类' }[c] || c || '-'
    },
    empTypeText(t) {
      return { MID: '中层', STAFF: '员工', EXEC: '高管' }[t] || t || '-'
    },
    /** 载入打分进度统计（仅统计管理员/计算角色；年度平均 season=0 无进度语义，跳过并清空） */
    loadProgress() {
      if (!this.canProgress) return
      if (!this.query.year || Number(this.query.season) === 0) { this.progress = null; return }
      this.progressLoading = true
      getProgress(this.query.year, this.query.season).then(response => {
        this.progress = response.data
        this.progressLoading = false
      }).catch(() => {
        this.progressLoading = false
      })
    },
    /** 完成度百分比（应打为 0 时记 0） */
    pct(done, total) {
      if (!total) return 0
      return Math.round(done / total * 100)
    },
    loadAll() {
      this.loadDept()
      this.loadEmp()
      this.loadProgress()
      // 同步刷新考核期状态：计算按钮可用性实时跟随管理员的开启/关闭操作
      getPeriods().then(response => {
        this.periods = response.data || []
      }).catch(() => {})
    },
    loadDept() {
      if (!this.showDeptSection) return // 无可查看的部门（非统计管理员且非负责人/分管领导）时不请求
      if (!this.query.year) return
      this.deptLoading = true
      getDeptResults(this.query.year, this.query.season).then(response => {
        this.deptRows = response.data || []
        this.deptOptions = this.deptRows.map(r => ({ deptId: r.deptId, deptName: r.deptName }))
        this.deptLoading = false
      }).catch(() => {
        this.deptLoading = false
      })
    },
    loadEmp() {
      if (!this.query.year) return
      this.empPage = 1
      this.empLoading = true
      getEmpResults(this.query.year, this.query.season, this.query.deptId).then(response => {
        this.empRows = response.data || []
        this.empLoading = false
      }).catch(() => {
        this.empLoading = false
      })
    },
    /** 当前考核期标签（用于导出文件名） */
    periodLabel() {
      return this.query.season ? '第' + this.query.season + '季度' : '年度平均'
    },
    /** 客户端导出 CSV（带 BOM，Excel 打开中文不乱码）；headers/rows 为字符串二维结构 */
    exportCsv(fileName, headers, rows) {
      const esc = v => {
        const s = (v === null || v === undefined) ? '' : String(v)
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
      }
      const lines = [headers.map(esc).join(',')]
      rows.forEach(r => lines.push(r.map(esc).join(',')))
      const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = fileName
      a.click()
      URL.revokeObjectURL(a.href)
    },
    exportDept() {
      if (!this.deptRows.length) {
        this.$message.warning('暂无部门结果可导出')
        return
      }
      // 所有角色导出一律只含最终得分（与页面列显隐一致；明细得分仅落库供审计）
      const headers = ['部门', '分类', '最终得分']
      const rows = this.deptRows.map(r => [r.deptName, this.categoryText(r.category), this.fmt(r.finalScore)])
      this.exportCsv('部门考核结果_' + this.query.year + '年_' + this.periodLabel() + '.csv', headers, rows)
    },
    exportEmp() {
      if (!this.sortedEmpRows.length) {
        this.$message.warning('暂无人员结果可导出')
        return
      }
      // 所有角色导出一律只含最终得分（与页面列显隐一致；明细得分仅落库供审计）
      const headers = ['姓名', '部门', '类型', '最终得分']
      const rows = this.sortedEmpRows.map(r => [r.employeeName, r.deptName, this.empTypeText(r.empType), this.fmt(r.finalScore)])
      this.exportCsv('人员考核结果_' + this.query.year + '年_' + this.periodLabel() + '.csv', headers, rows)
    },
    doCalc() {
      if (!this.canCalc) {
        this.$message.warning('无权限执行考核结果计算（需 KAOHE_CAL 角色）')
        return
      }
      if (!this.query.year || !this.query.season) {
        this.$message.warning('请选择年份和季度')
        return
      }
      if (!this.periodOpen) {
        this.$message.warning('当前所选期次未开启或已关闭，无法计算结果')
        return
      }
      // 二次确认：大字突出本次计算的期次，防止选错季度误覆盖
      this.$confirm(
        '<div style="font-size: 15px; line-height: 1.8;">即将重新计算并覆盖以下期次的全部考核结果：</div>' +
        '<div style="font-size: 28px; font-weight: bold; color: #F56C6C; text-align: center; margin: 12px 0;">' +
        this.query.year + ' 年 第 ' + this.query.season + ' 季度</div>' +
        '<div style="font-size: 13px; color: #909399;">该季度全部部门 / 人员结果将按当前评分明细重新生成并覆盖，请再次核对期次。</div>',
        '计算期次二次确认',
        {
          confirmButtonText: '确认计算 ' + this.query.year + ' 年第 ' + this.query.season + ' 季度',
          cancelButtonText: '取消',
          type: 'warning',
          dangerouslyUseHTMLString: true
        }
      ).then(() => {
        this.calculating = true
        calcResults(this.query.year, this.query.season).then(response => {
          this.calculating = false
          const d = response.data || {}
          this.$notify({
            title: '成功',
            message: '计算完成：部门 ' + (d.deptCount || 0) + ' 条，人员 ' + (d.empCount || 0) + ' 条',
            type: 'success',
            duration: 3000
          })
          this.loadAll()
        }).catch(() => {
          this.calculating = false
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
/* ===== 轻量视觉优化（沿用项目 element-ui 原有配色，不引入新色系） ===== */
::v-deep .el-table th {
  background: #f5f7fa !important;
  color: #606266;
  font-weight: 600;
}
::v-deep .el-table tbody tr:hover > td {
  background: #ecf5ff !important;
}
::v-deep .el-button--primary {
  border-radius: 4px;
}
/* 结果区分节标题：主题蓝竖条 */
.perf-section-title {
  margin: 10px 0;
  padding-left: 8px;
  border-left: 3px solid #409EFF;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}
/* 分节标题行：左标题、右导出按钮 */
.perf-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 10px 0;
}
.perf-section-head .perf-section-title {
  margin: 0;
}
/* 最终得分：主题蓝加粗（与原内联样式一致） */
.final-score {
  font-weight: bold;
  color: #409EFF;
}
</style>

